<template>
    <model-modal
        :model-class="modelClass"
        @opened="onOpened"
        content-classes="flex flex-col h-full overflow-y-auto items-center"
        slotContainerClasses="w-full h-full relative"
        ref="modal"
    >
        <template v-slot:default="slotProps">
            <template v-if="slotProps.model">
                <div class="flex flex-col h-full">
                    <div class="p-8 border-b border-gray-300">
                        <h2 class="text-xl font-thin text-gray-800">
                            <template v-if="! model.worksite">
                                {{ model.title }}
                            </template>
                            <template v-else>
                                <template v-if="model.worksitePhase">
                                    <span class="block">{{ model.worksitePhase.title }}</span>
                                    <span class="flex items-center space-x-2 text-base">
                                        <span>{{ model.worksite.title }}</span>
                                        <span
                                            class="cursor-pointer"
                                            @click.prevent.stop="model.previewProfitabilityDocument()" 
                                            v-if="model.hasProfitabilityDocument()" 
                                            data-tooltip-location="right" 
                                            :data-tooltip="$t('time-activity.labels.has_profitability_document')"
                                        >
                                            <i class="text-sm far fa-check-circle"></i>
                                        </span>
                                    </span>
                                </template>
                                <template v-else>
                                    <span class="flex items-center space-x-2 text-base">
                                        <span>{{ model.worksite.title }}</span>
                                        <span
                                            class="cursor-pointer"
                                            @click.prevent.stop="model.previewProfitabilityDocument()" 
                                            v-if="model.hasProfitabilityDocument()" 
                                            data-tooltip-location="right" 
                                            :data-tooltip="$t('time-activity.labels.has_profitability_document')"
                                        >
                                            <i class="text-sm far fa-check-circle"></i>
                                        </span>
                                    </span>
                                </template>
                            </template>
                        </h2>
                    </div>
                    <div class="flex flex-wrap p-8 border-b border-gray-300 xl:space-x-8 xl:flex-no-wrap">
                        <div class="w-full xl:w-[300px] mb-8 xl:mb-0" v-if="hasGeolocation">
                            <div id="geolocation-map" class="h-[220px] bg-gray-200 rounded"></div>
                        </div>
                        <div class="flex-1">
                            <div class="mb-4" v-if="model.trustUpIOTask">
                                <p class="text-sm text-gray-600">
                                    {{ $t('time-activity.labels.task') }}
                                </p>
                                <trustup-io-task :task="model.trustUpIOTask"></trustup-io-task>
                            </div>
                            <div class="mb-4" v-if="model.worksite">
                                <p class="text-sm text-gray-600">
                                    {{ $t('time-activity.labels.title') }}
                                </p>
                                <p class="text-gray-900">
                                    {{ model.title }}
                                </p>
                            </div>
                            <div class="mb-4">
                                <p class="text-sm text-gray-600">
                                    {{ $t('time-activity.labels.date') }}
                                </p>
                                <p class="text-gray-900">
                                    {{ model.getStartsAt().format('dddd, DD MMMM YYYY') }}
                                </p>
                            </div>
                            <div class="mb-4">
                                <p class="text-sm text-gray-600">
                                    {{ $t('time-activity.labels.time') }}
                                </p>
                                <p class="text-gray-900" v-if="model.getEndsAtOrNull()">
                                    {{ model.getStartsAt().format('HH:mm') }} - {{ model.getEndsAt().format('HH:mm') }}
                                </p>
                                <p class="text-gray-900" v-else>
                                    {{ $t('time-activity.in_progress_time', {
                                        time: model.getStartsAt().format('HH:mm')
                                    }) }}
                                </p>
                            </div>
                            <div class="mb-4">
                                <p class="text-sm text-gray-600">
                                    {{ $t('time-activity.labels.duration') }}
                                </p>
                                <p class="text-gray-900">
                                    <live-time-difference
                                        class-name="text-base text-gray-900"
                                        container-class-name="w-auto"
                                        ref="timeDifference"
                                        :activity="model"
                                    ></live-time-difference>
                                </p>
                            </div>
                            <div class="mb-4">
                                <p class="text-sm text-gray-600">
                                    {{ $t('time-activity.labels.user') }}
                                </p>
                                <p class="text-gray-900">
                                    {{ model.user ? model.user.getName() : $t('time-activity.no_user') }}
                                </p>
                            </div>
                            <!-- <div class="mb-4" v-if="actimodelvity.geolocation.start">
                                <p class="text-sm text-gray-600">{{ $t('modals.activity_geolocation.locations.start') }}</p>
                                <p class="text-gray-900">
                                    {{ model.geolocation.start.latitude }}, {{ model.geolocation.start.longitude }}
                                </p>
                            </div>
                            <div class="mb-4" v-if="model.geolocation.end">
                                <p class="text-sm text-gray-600">{{ $t('modals.activity_geolocation.locations.end') }}</p>
                                <p class="text-gray-900">
                                    {{ model.geolocation.end.latitude }}, {{ model.geolocation.end.longitude }}
                                </p>
                            </div>
                            <div class="mb-4" v-if="model.geolocation.pool && model.geolocation.pool.length > 0">
                                <p class="text-sm text-gray-600">{{ $t('modals.activity_geolocation.locations.pool') }}</p>
                                <p class="text-gray-900" v-for="(pool, index) in model.geolocation.pool" :key="`pool-${index}`">
                                    {{ pool.latitude }}, {{ pool.longitude }} ({{ pool.created_at | moment().format('dddd DD MMMM YYYY [à] HH:mm:ss') }})
                                </p>
                            </div> -->
                        </div>
                    </div>
                    <div class="p-8 border-b border-gray-300" v-if="pauses">
                        <div v-for="(pause, index) in pauses" class="mb-4" :key="pause.uuid">
                            <p class="text-sm text-gray-600">
                                {{ $t('time-activity.labels.pause_count', {count: (index+1)}) }}
                            </p>
                            <p class="text-gray-900">
                                <span v-if="pause.ends_at">
                                    {{ pause.starts_at | moment().format('HH:mm:ss') }} - {{ pause.ends_at | moment().format('HH:mm:ss') }}
                                </span>
                                <span v-else>
                                    {{ $t('time-activity.labels.current_pause', {start: $moment(pause.starts_at).format('HH:mm:ss')}) }}
                                </span>
                            </p>
                        </div>
                    </div>
                    <div class="flex-1 p-8">
                        <h2 class="mb-4 text-lg">
                            {{ $t('time-activity.labels.comments') }}
                        </h2>
                        <trustup-conversation
                            display-style="comments"
                            v-bind="model.trustup_io.model"
                        />
                    </div>
                </div>
            </template>
        </template>
    </model-modal>
</template>

<script>
    import Map from '~spa/classes/Map';
    import ModelModal from '@spa/apps/trustup-pro-base-js/components/Model/Modal';
    import TimeActivity from '@spa/apps/trustup-pro-planning-js/models/TimeActivity';
    import TrustupConversation from "@deegital/vuejs-trustup-io-messaging";
    import TrustupIOTask from '@spa/apps/trustup-pro-base-js/components/TrustUpIO/Task/Item';
    import LiveTimeDifference from '@spa/apps/trustup-pro-planning-js/components/TimeActivity/_Partials/LiveTimeDifference';
    
    export default {
        components: {
            ModelModal,
            TrustupConversation,
            LiveTimeDifference,
            'trustup-io-task': TrustupIOTask,
        },

        data() {
            return {
                modelClass: TimeActivity,
                pauses: null,
                map: new Map(16, {disableDefaultUI: false, mapTypeId: 'satellite'})
            }
        },

        computed: {
            hasGeolocation()
            {
                return this.model && this.model.geolocation && (this.model.geolocation.start || this.model.geolocation.end);
            },
            model()
            {
                return this.$refs.modal?.model || null;
            },
        },
        
        methods: {
            async onOpened()
            {
                await this.$nextTick();
                this.createMap();
                this.loadPauses();
            },

            async loadPauses()
            {
                this.pauses = await window.Planning.endpoints.timeActivity.getPauses(this.model.uuid);
                console.log(this.pauses);
            },

            async createMap()
            {
                if ( ! this.hasGeolocation ) {
                    console.warn('no geolocation found.');
                    return;
                }
                
                await this.$store.dispatch('loadGoogleMaps');

                this.map.center = this.model.geolocation.start
                    ? {
                        lat: this.model.geolocation.start.latitude,
                        lng: this.model.geolocation.start.longitude
                    }
                    : {
                        lat: this.model.geolocation.end.latitude,
                        lng: this.model.geolocation.end.longitude
                    };

                this.map.createMap('geolocation-map');

                let markers = [];

                if ( this.model.geolocation.start ) {
                    markers.push(this.createMarker('start', this.model.geolocation.start));
                }
                if ( this.model.geolocation.end ) {
                    markers.push(this.createMarker('end', this.model.geolocation.end));
                }
                for ( let i = 0; i < this.model.geolocation.pool.length; i++ ) {
                    markers.push(this.createMarker('pool', this.model.geolocation.pool[i]));
                }

                this.map.createMarkers(markers);
            },

            createMarker(type, location)
            {
                return {
                    icon: this.map.getLocationIcon(type),
                    position: {
                        lat: location.latitude,
                        lng: location.longitude
                    }
                };
            },
        },
    }
</script>